import type { QueryClient, QueryKey } from '@tanstack/react-query';

import { DEFAULT_DAYPART_TAG, SMOLT_DAYPART_TAG } from '@/contexts/cageFilter/params';
import { welfareImagesParams } from '@/domain/images/useGetWelfareImages';
import {
    useCageController_Context,
    useCageController_Overview,
    useWelfareController_allImages,
} from '@/services/hooks';
import type { WelfareController_allImagesQueryParams } from '@/services/types';
import { MERGED_WELFARE_IMAGES_TYPE } from '@/utils/welfareImageHelpers';

import {
    CAGE_OVERVIEW_STALE_TIME_MS,
    cageOverviewQueryKey,
    cageOverviewQueryParams,
} from './cageOverviewQuery';
import type { UseGetCageOverviewParams } from './overview.types';
import { cageContextQueryParams } from './useGetCageContext';

/**
 * One pen's cage dashboard, as the destination page will ask for it.
 *
 * `from` and `to` are omitted from the object when the page has no range; the
 * overview key still carries them as undefined. `fishGroupId` is omitted when
 * the page is not scoped to a cohort.
 */
export interface CageDashboardPrefetchTarget {
    cageId: number;
    fishGroupId?: number;
    from?: string;
    to?: string;
    daypartTag: string;
}

const PREFETCH_OPTIONS = { staleTime: CAGE_OVERVIEW_STALE_TIME_MS };

/**
 * The newest full-size detection the dashboard's latest-image card asks for.
 * This is that card's image list, not the welfare or lice overview.
 */
export function latestDashboardImageParams(
    fishGroupId?: number
): WelfareController_allImagesQueryParams {
    return welfareImagesParams({
        type: MERGED_WELFARE_IMAGES_TYPE,
        numberOfImages: '1',
        fishGroupId,
    });
}

function overviewParamsOf(target: CageDashboardPrefetchTarget): UseGetCageOverviewParams {
    return {
        from: target.from,
        to: target.to,
        daypartTag: target.daypartTag,
        ...(target.fishGroupId != null ? { fishGroupId: target.fishGroupId } : {}),
    };
}

function isFresh(client: QueryClient, queryKey: QueryKey): boolean {
    const state = client.getQueryState(queryKey);
    // Invalidation leaves the old timestamp. Inactive queries are not refetched,
    // and this client does not refetch on mount, so a warm reading would keep
    // data from before the change (for example a demo-mode switch).
    if (state?.data === undefined || state.status !== 'success' || state.isInvalidated) {
        return false;
    }
    return Date.now() - state.dataUpdatedAt < CAGE_OVERVIEW_STALE_TIME_MS;
}

/** The overview key still has usable data: inside the stale window, and not invalidated. */
export function isCageOverviewCacheWarm(
    client: QueryClient,
    target: CageDashboardPrefetchTarget
): boolean {
    return isFresh(client, cageOverviewQueryKey(target.cageId, overviewParamsOf(target)));
}

/** Context, overview and the latest image are all still fresh and not invalidated. */
export function isCageDashboardCacheWarm(
    client: QueryClient,
    target: CageDashboardPrefetchTarget
): boolean {
    const overview = overviewParamsOf(target);
    const contextParams = cageContextQueryParams(target.fishGroupId);
    const imageParams = latestDashboardImageParams(target.fishGroupId);
    return (
        isFresh(client, useCageController_Context.info(target.cageId, contextParams).key) &&
        isFresh(client, cageOverviewQueryKey(target.cageId, overview)) &&
        isFresh(client, useWelfareController_allImages.info(target.cageId, imageParams).key)
    );
}

/**
 * Warm the three requests the cage dashboard needs before its own cards start:
 * cage context, the overview, and the latest detection image.
 *
 * Lice, welfare and health stay cold. Each prefetch uses the overview's stale
 * time, which is also the client default the other two hooks inherit, so a
 * later mount reads the cache instead of sending the request again.
 */
export function prefetchCageDashboard(
    client: QueryClient,
    target: CageDashboardPrefetchTarget
): Promise<void> {
    const overviewParams = cageOverviewQueryParams(overviewParamsOf(target));
    const contextParams = cageContextQueryParams(target.fishGroupId);
    const imageParams = latestDashboardImageParams(target.fishGroupId);

    return Promise.all([
        useCageController_Context.prefetch(client, target.cageId, contextParams, PREFETCH_OPTIONS),
        useCageController_Overview.prefetch(
            client,
            target.cageId,
            overviewParams,
            PREFETCH_OPTIONS
        ),
        useWelfareController_allImages.prefetch(
            client,
            target.cageId,
            imageParams,
            PREFETCH_OPTIONS
        ),
    ]).then(() => undefined);
}

/**
 * A location-table pen link: the dashboard, that row's fish group, no date range.
 * A smolt production tank reads `all`; every other pen reads `day`. The pen's
 * cage type decides, including a tank on a land-based site.
 */
export function locationTableCageDashboardTarget(input: {
    cageId: number;
    fishGroupId?: number;
    smolt: boolean;
}): CageDashboardPrefetchTarget {
    return {
        cageId: input.cageId,
        ...(input.fishGroupId != null ? { fishGroupId: input.fishGroupId } : {}),
        daypartTag: input.smolt ? SMOLT_DAYPART_TAG : DEFAULT_DAYPART_TAG,
    };
}
